import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { tokens, useTheme } from "../../glassTheme.tsx";
import { GlassFloat } from "./Glass.tsx";
import {
  IconBoard, IconBody, IconCalendar, IconJournal, IconMoney, IconSearch, IconSessions,
} from "./navIcons.tsx";

export type Module = "sessions" | "board" | "money" | "body" | "journal" | "calendar";

const MODULES: { key: Module; label: string; Icon: (p: { size?: number }) => React.ReactElement }[] = [
  { key: "sessions", label: "Sessions", Icon: IconSessions },
  { key: "board", label: "Board", Icon: IconBoard },
  { key: "money", label: "Money", Icon: IconMoney },
  { key: "body", label: "Body", Icon: IconBody },
  { key: "journal", label: "Journal", Icon: IconJournal },
  { key: "calendar", label: "Calendar", Icon: IconCalendar },
];

const LONG_PRESS_MS = 500;

/**
 * The one floating glass pill, six modules, always visible (§0). Settings —
 * model, effort, the manual light/dark override, and everything not yet
 * given a home in the six (Graph, Workout, Ideas, Build) — is reached from a
 * long press on the pill rather than a seventh icon or a visible "..."
 * button, per the spec's own note on Settings. `active` is nullable: on a
 * screen the new nav doesn't yet own (still reached only through the long
 * press), none of the six read as current.
 *
 * Search is a seventh way in that does not fit inside the pill: at 412px with
 * the +add button beside it the pill's inner width is 304px and the six items
 * already need all of it, so a seventh — labelled or a bare 48px icon — could
 * only be had by shrinking the six. It is a round glass float of its own at
 * the pill's end instead, in the same right-hand column as +add, above it, so
 * +add stays exactly where it was. It is there on every screen, +add or not,
 * so the pill is one width everywhere.
 */
export function Nav({
  active, onNavigate, onLongPress, fab, onSearch, searchActive,
}: {
  active: Module | null;
  onNavigate: (m: Module) => void;
  onLongPress: () => void;
  onSearch: () => void;
  searchActive: boolean;
  /** Absent on Sessions, and on anything not reached through the pill yet — the spec gives +add to the five non-Sessions modules only. */
  fab?: { onClick: () => void } | null;
}) {
  const { colors } = useTheme();
  const pressTimer = useRef<number | null>(null);
  const firedLongPress = useRef(false);

  const startPress = () => {
    firedLongPress.current = false;
    pressTimer.current = window.setTimeout(() => {
      firedLongPress.current = true;
      onLongPress();
    }, LONG_PRESS_MS);
  };
  // Text size on this phone is not ours to know: at Samsung's larger font
  // settings "Calendar" no longer fits its share of the pill. So the labels
  // are measured once per size change and, if any would clip, all shrink
  // together by just enough. At normal size this changes nothing.
  const pill = useRef<HTMLDivElement>(null);
  const [labelScale, setLabelScale] = useState(1);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const remeasure = () => { setLabelScale(1); setTick((t) => t + 1); };
    window.addEventListener("resize", remeasure);
    document.fonts?.ready.then(remeasure);
    return () => window.removeEventListener("resize", remeasure);
  }, []);
  useLayoutEffect(() => {
    if (labelScale !== 1 || !pill.current) return;
    let ratio = 1;
    pill.current.querySelectorAll<HTMLElement>("[data-label]").forEach((el) => {
      if (el.scrollWidth > el.clientWidth) ratio = Math.min(ratio, el.clientWidth / el.scrollWidth);
    });
    if (ratio < 1) setLabelScale(Math.max(0.6, ratio * 0.97));
  }, [tick, labelScale]);

  const endPress = () => {
    if (pressTimer.current !== null) { clearTimeout(pressTimer.current); pressTimer.current = null; }
  };

  return (
    <div style={{
      position: "fixed", left: 0, right: 0, bottom: 0, maxWidth: 560, margin: "0 auto",
      display: "flex", flexDirection: "column", alignItems: "stretch", gap: 10,
      // The gesture bar's height comes from env(); the side insets matter in
      // landscape, where the cutout sits at one end.
      padding: `0 max(${tokens.space.dockInset}px, env(safe-area-inset-right)) calc(var(--nav-gap) + env(safe-area-inset-bottom)) max(${tokens.space.dockInset}px, env(safe-area-inset-left))`,
      zIndex: 30, pointerEvents: "none",
    }}>
      {/* Search and +add sit in a row above the pill, right-aligned, rather
          than beside it: six items at 48px each and two 48px buttons do not
          fit across 360px, and the pill would have to shrink its targets. */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, pointerEvents: "none" }}>
        <GlassFloat style={{ pointerEvents: "auto" }}>
          <button
            onClick={onSearch}
            aria-label="Search" aria-pressed={searchActive}
            style={{
              width: tokens.size.fab, height: tokens.size.fab, borderRadius: "50%",
              display: "grid", placeItems: "center", border: "none", cursor: "pointer",
              background: searchActive ? colors.accentSoft : "transparent",
              color: searchActive ? colors.ink : colors.ink2,
            }}
          ><IconSearch size={tokens.size.navIcon} /></button>
        </GlassFloat>

        {fab && (
          <GlassFloat style={{ pointerEvents: "auto" }}>
            <button
              onClick={fab.onClick}
              aria-label="Add"
              style={{
                width: tokens.size.fab, height: tokens.size.fab, borderRadius: "50%",
                display: "grid", placeItems: "center", border: "none", background: "transparent",
                color: colors.ink, fontSize: 24, fontWeight: 300, lineHeight: 1, cursor: "pointer",
              }}
            >+</button>
          </GlassFloat>
        )}
      </div>

      <GlassFloat style={{ padding: "6px", pointerEvents: "auto", minWidth: 0 }}>
        <div
          onTouchStart={startPress} onTouchEnd={endPress} onTouchCancel={endPress}
          onMouseDown={startPress} onMouseUp={endPress} onMouseLeave={endPress}
          ref={pill} style={{ display: "flex", alignItems: "stretch", gap: 0 }}
        >
          {MODULES.map(({ key, label, Icon }) => {
            const on = key === active;
            return (
              <button
                key={key}
                onClick={() => { if (!firedLongPress.current) onNavigate(key); }}
                aria-label={label}
                style={{
                  // Equal shares of the pill, so it is exactly as wide as the
                  // screen allows and no item is ever pushed off the end.
                  flex: "1 1 0", minWidth: 0, minHeight: 56,
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3,
                  padding: "6px 0", borderRadius: 999,
                  border: "none", background: on ? colors.accentSoft : "transparent",
                  color: on ? colors.ink : colors.ink2, cursor: "pointer",
                  fontFamily: tokens.font.sans, fontSize: tokens.type.navLabel.size,
                  letterSpacing: tokens.type.navLabel.letterSpacing,
                }}
              >
                <Icon size={tokens.size.navIcon} />
                <span data-label style={{
                  maxWidth: "100%", overflow: "hidden", textOverflow: "clip", whiteSpace: "nowrap",
                  fontSize: labelScale === 1 ? undefined : `${tokens.type.navLabel.size * labelScale}px`,
                }}>{label}</span>
              </button>
            );
          })}
        </div>
      </GlassFloat>
    </div>
  );
}
